import React from 'react';
import { CenteredContainer, Link, Button } from '@nerdwallet/currency';
import Image from 'next/image';
import FolderTabContainer from '../folder-tab-container/folder-tab-container';
import styles from './app-download.module.scss';
import './app-download-carousel.scss';
import SegmentImpression from '../../segment-impression';
import { HOMEPAGE_SEGMENT_INSTRUMENTATION } from '~/app/lib/analytics/analytics-constants';
import { BASE_IMAGE_URL } from '../constants';
import useAnalytics from '~/app/lib/segment/useAnalytics';
import { TreasuryRates } from '~/app/queries/types';

interface AnalyticsProps {
  entity_name: string;
  location: string;
  sub_location: string;
}

interface Props {
  backgroundColor?: string;
  negativeTopMarginValue?: number;
  treasuryRates?: TreasuryRates;
}

const getAPYValue = (
  treasuryRates: TreasuryRates | undefined,
): string | undefined => {
  if (treasuryRates?.treasuryAccountMarketing?.annualPercentageYield) {
    return (
      Math.ceil(
        (treasuryRates.treasuryAccountMarketing.annualPercentageYield ?? 0) *
          100,
      ) / 100
    ).toFixed(2);
  }
  return undefined;
};

const getAPYDateValue = (
  treasuryRates: TreasuryRates | undefined,
): string | undefined => {
  if (treasuryRates?.treasuryAccountMarketing?.lastFetchedAt) {
    return new Date(
      treasuryRates.treasuryAccountMarketing.lastFetchedAt,
    ).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  }
  return undefined;
};

const AppDownloads = ({
  backgroundColor = '#006642',
  negativeTopMarginValue = 31,
  treasuryRates,
}: Props): JSX.Element => {
  const appLandingPageAnalytics: AnalyticsProps = {
    entity_name: HOMEPAGE_SEGMENT_INSTRUMENTATION.app_module.entity_name,
    location: HOMEPAGE_SEGMENT_INSTRUMENTATION.app_module.location,
    sub_location: HOMEPAGE_SEGMENT_INSTRUMENTATION.app_module.sub_location,
  };

  const { trackSegmentTrackEvent } = useAnalytics();
  const handleAnalyticsClick = (analytics: AnalyticsProps) => {
    trackSegmentTrackEvent('Element Interaction', analytics);
  };

  const APY = getAPYValue(treasuryRates);
  const apyDate = getAPYDateValue(treasuryRates);

  return (
    <FolderTabContainer
      tabBackgroundColor={backgroundColor}
      contentBackgroundColor={backgroundColor}
      negativeTopMarginValue={negativeTopMarginValue}
      noMobileTopMargin={true}
    >
      <CenteredContainer>
        <div className={styles.container}>
          <div className={styles.appDownloadsContent}>
            {/* NerdWallet app star rating */}
            <div className={styles.appDownloadsLeftContainer}>
              <Image
                className={styles.appDownloadLogo}
                alt="Nerdwallet Logo"
                width="80"
                height="80"
                src={`${BASE_IMAGE_URL}/superbowl/front_page_logo.png`}
              />
              <h2 className={styles.title}>
                Track, save, and invest — all with one easy app
              </h2>
              <p className={styles.appDownloadParagraph}>
                <Image
                  alt="Green check"
                  width="24"
                  height="24"
                  src={`${BASE_IMAGE_URL}/superbowl/front_page_check.png`}
                ></Image>
                Monitor your wealth the smart way with our net worth tracker.
              </p>
              <p className={styles.appDownloadParagraph}>
                <Image
                  alt="Green check"
                  width="24"
                  height="24"
                  src={`${BASE_IMAGE_URL}/superbowl/front_page_check.png`}
                ></Image>
                Grow your cash and earn {APY ? `${APY}%` : 'competitive'} APY
                with a Treasury account offered by Atomic Invest.*
              </p>
              <p className={styles.appDownloadParagraph}>
                <Image
                  alt="Green check"
                  width="24"
                  height="24"
                  src={`${BASE_IMAGE_URL}/superbowl/front_page_check.png`}
                ></Image>
                Build wealth your way with an Automated Investing account
                offered by Atomic Invest.{' '}
              </p>
              <SegmentImpression
                eventName="Element Impression"
                eventProps={appLandingPageAnalytics}
              >
                <a href="/nerdwallet-app-track-your-wealth-and-build-your-future">
                  <button
                    className={styles.appDownloadButton}
                    onClick={() =>
                      handleAnalyticsClick(appLandingPageAnalytics)
                    }
                  >
                    LEARN MORE
                  </button>
                </a>
              </SegmentImpression>
              <div className={styles.appDownloadsCardContainer}>
                <div className={styles.appNumberDesktopImg}>
                  <Image
                    src={`${BASE_IMAGE_URL}/superbowl/App_Number_One_2x.png`}
                    alt="average star rating"
                    width={424}
                    height={262}
                  />
                </div>
                <div className={styles.appNumberDesktopImg}>
                  <Image
                    src={`${BASE_IMAGE_URL}/superbowl/App_Number_Two_2x.png`}
                    alt="5-star ratings"
                    width={424}
                    height={262}
                  />
                </div>
                <div className={styles.appNumberDesktopImg}>
                  <Image
                    src={`${BASE_IMAGE_URL}/superbowl/App_Number_Three_2x.png`}
                    alt="total downloads"
                    width={424}
                    height={262}
                  />
                </div>
                <div className={styles.appNumberMobileImg}>
                  <Image
                    src={`${BASE_IMAGE_URL}/superbowl/App_Number_One_375_2x.png`}
                    alt="average star rating"
                    width={215}
                    height={133}
                  />
                </div>
                <div className={styles.appNumberMobileImg}>
                  <Image
                    src={`${BASE_IMAGE_URL}/superbowl/App_Number_Two_375_2x.png`}
                    alt="5-star ratings"
                    width={215}
                    height={133}
                  />
                </div>
                <div className={styles.appNumberMobileImg}>
                  <Image
                    src={`${BASE_IMAGE_URL}/superbowl/App_Number_Three_375_2x.png`}
                    alt="total downloads"
                    width={215}
                    height={133}
                  />
                </div>
              </div>
            </div>
            {/* NerdWallet app screenshot */}
            <div className={styles.smartphoneDesktopImg}>
              <Image
                alt="NerdWallet app"
                height={1105}
                width={1114}
                className={styles.QRCodeFeaturedImage}
                src={`${BASE_IMAGE_URL}/superbowl/app_screen_language.png`}
                sizes="(max-width: 1024px) 100vw, 90vw"
              />
            </div>
            <div className={styles.smartphoneMobileImg}>
              <Image
                alt="NerdWallet app"
                height={599}
                width={750}
                className={styles.QRCodeFeaturedImage}
                src={`${BASE_IMAGE_URL}/superbowl/mobile_app_screen_language.png`}
                sizes="(max-width: 768px) 100vw"
              />
            </div>
          </div>
        </div>
      </CenteredContainer>
      <div className={styles.footNote}>
        <p>
          {APY && apyDate
            ? `*The annualized yield for a Treasury Bill (“T-Bill”) maturing in one month as of ${apyDate} when held to maturity is ${APY}%. Rate shown is gross of fees. `
            : '*'}
          Annual percentage yield (APY) is the nominal interest rate of the
          T-Bill maturing in one month with 12 compounding periods per year.
          Annualized yield and APY are investment characteristics of the T-bill
          and should not be regarded as performance or a projection of
          performance of any Treasury account. The effective return on T-Bills
          will be reduced by fees which has a compounding impact on returns over
          time.
        </p>
      </div>
    </FolderTabContainer>
  );
};

export default AppDownloads;
